import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreateSubmissionPayloadDto,
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  SubmissionCheckResponseDtoResultEnum,
  SubmissionResponseDto,
} from 'libs/pinnacle-sdk';
import { DocumentIcon } from 'assets/icons/document';
import CustomBadge from '../../ui/custom-badge';
import { ClipboardIcon } from 'assets/icons/clipboard';
import { UserTagIcon } from 'assets/icons/user-tag';
import { format } from 'date-fns';
import { message } from 'antd';
import InfoModal from 'components/ui/info-modal';
import ValidationItem from 'components/ui/validation-item';
import CopyIcon from 'assets/icons/copy';
import { insuredApi, submissionApi, userApi } from 'services/apis';
import {
  AUTO_NAVIGATE_DELAY,
  StatusCheck,
} from 'components/features/submission-review/types';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { debounce } from 'lodash';
import { useMutation, useQuery } from 'react-query';
import { handleError } from 'utils/form';
import { CustomFile } from 'components/features/file-uploader/types';
import ReviewSummary from 'components/ui/review-summary';
import UserInfo from 'components/ui/user-info';
import { SummaryData } from 'components/ui/review-summary/types';
import { useTranslation } from 'react-i18next';
import { EQueryKeys } from 'constants/query-keys';
import { useNavigate } from 'react-router';
import { constructSubmissionDetailPath } from 'constants/path';
import { getLocalDateForMidnightUTC } from 'utils';

export const SubmissionReview: React.FC<{
  submission: CreateSubmissionPayloadDto;
  onEdit: () => void;
  onCancel: () => void;
  fileList: CustomFile[];
  sessionId: string;
  entityType: CreateUploadedFilePayloadDtoEntityTypeEnum;
}> = ({ submission, onEdit, onCancel, fileList, sessionId, entityType }) => {
  const { t } = useTranslation();
  const [showCheck, setShowCheck] = useState(false);
  const [showProceedCreate, setShowProceedCreate] = useState(false);
  const [showUnderReview, setShowUnderReview] = useState(false);
  const [statusChecks, setStatusChecks] = useState<StatusCheck>({
    duplicateCheck: 'Pending',
  });
  const [submissionId, setSubmissionId] = useState<string>('');
  const [autoNavigateCountdown, setAutoNavigateCountdown] = useState<
    number | null
  >(null);
  const navigate = useNavigate();

  const getDuplicateCheckStatus = useCallback(async () => {
    return submissionApi.checkDuplicatedSubmission({
      submissionDuplicationCheckPayloadDto: {
        inceptionDate: submission.inceptionDate,
        insuredId: submission.insuredId,
        lineOfBusiness: submission.lineOfBusiness,
      },
    });
  }, [
    submission.inceptionDate,
    submission.insuredId,
    submission.lineOfBusiness,
  ]);
  const closeUnderReviewAndNavigate = useCallback(() => {
    setShowUnderReview(false);
    navigate(constructSubmissionDetailPath(submissionId));
  }, [navigate, submissionId]);

  const { data: insured, isLoading: isLoadingInsured } = useQuery(
    [EQueryKeys.INSURED_BY_ID, { insuredId: submission.insuredId }],
    async () => {
      return insuredApi.getInsuredById({
        insuredId: submission.insuredId,
      });
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: assignedHandler, isLoading: isLoadingAssignedHandler } =
    useQuery(
      [
        EQueryKeys.ASSIGNED_HANDLER_BY_ID,
        {
          userId: submission.assignedHandlerId,
        },
      ],
      async () => {
        return userApi.getUserById({
          userId: submission.assignedHandlerId,
        });
      },
      {
        refetchOnWindowFocus: false,
      }
    );

  const { data: namedUnderwriter, isLoading: isLoadingNamedUnderwriter } =
    useQuery(
      [EQueryKeys.NAMED_UNDERWRITER_BY_ID, submission.namedUnderwriterId],
      async () => {
        if (!submission.namedUnderwriterId) return null;
        return userApi.getUserById({ userId: submission.namedUnderwriterId });
      },
      {
        refetchOnWindowFocus: false,
      }
    );

  const createSubmissionMutation = useMutation(
    (submission: CreateSubmissionPayloadDto) =>
      submissionApi.createSubmission({
        createSubmissionPayloadDto: {
          ...submission,
          fileIds: fileList
            .filter((file) => !!file.id)
            .map((file) => file.id) as string[],
        },
      }),
    {
      onSuccess: (data) => {
        setSubmissionId(data.submissionId);
        message.success(t('submission.recordCreatedSuccessfully'));
      },
      onError: async (error: { response: Response }) =>
        handleError({
          error,
          defaultMessage: t('submission.recordCreatedFailed'),
        }),
    }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCreateSubmissionMutate = useCallback(
    debounce(
      (
        submission: CreateSubmissionPayloadDto,
        onSuccess?: (data: SubmissionResponseDto) => void,
        onError?: () => void
      ) => {
        createSubmissionMutation.mutate(submission, {
          onSuccess,
          onError,
        });
      },
      500
    ),
    [createSubmissionMutation]
  );

  const proceedCreateSubmission = useCallback(() => {
    setShowProceedCreate(false);
    debouncedCreateSubmissionMutate(submission, () => {
      setShowUnderReview(true);
    });
  }, [
    debouncedCreateSubmissionMutate,
    setShowProceedCreate,
    submission,
    setShowUnderReview,
  ]);

  const hideShowCheckAndResetCountDown = useCallback(() => {
    setShowCheck(false);
    setAutoNavigateCountdown(null); // Clear countdown when modal is closed
  }, []);

  const handleSubmissionCheckResult = useCallback(() => {
    const havingCheckNotPassed = Object.values(statusChecks).some(
      (status) => status !== SubmissionCheckResponseDtoResultEnum.Passed
    );
    hideShowCheckAndResetCountDown();
    if (havingCheckNotPassed) {
      return setShowProceedCreate(true);
    }
    debouncedCreateSubmissionMutate(submission, (data) =>
      navigate(constructSubmissionDetailPath(data.submissionId))
    );
  }, [
    debouncedCreateSubmissionMutate,
    navigate,
    statusChecks,
    submission,
    hideShowCheckAndResetCountDown,
  ]);

  // Auto-navigate countdown effect
  useEffect(() => {
    if (autoNavigateCountdown !== null && autoNavigateCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoNavigateCountdown(autoNavigateCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (autoNavigateCountdown === 0) {
      handleSubmissionCheckResult();
    }
  }, [autoNavigateCountdown, handleSubmissionCheckResult]);

  // Watch for all checks to pass and start countdown
  useEffect(() => {
    if (!showCheck) {
      return;
    }
    const allChecksPassed = Object.values(statusChecks).every(
      (status) => status === SubmissionCheckResponseDtoResultEnum.Passed
    );

    // Start countdown when all checks pass and modal is open
    if (allChecksPassed && autoNavigateCountdown === null) {
      setAutoNavigateCountdown(AUTO_NAVIGATE_DELAY);
    }
  }, [statusChecks, showCheck, autoNavigateCountdown]);

  useEffect(() => {
    return () => {
      debouncedCreateSubmissionMutate.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summaryData: SummaryData = useMemo(
    () => ({
      title: t('submission.newSubmissionTitle'),
      sections: [
        {
          title: t('submission.submissionInformation'),
          icon: <DocumentIcon />,
          items: [
            {
              label: t('submission.submissionStatus'),
              value: (
                <CustomBadge
                  label={submission.status}
                  backgroundColor="bg-blue-100"
                  textColor="text-blue-600"
                />
              ),
            },
            {
              label: t('submission.policyType'),
              value: (
                <CustomBadge
                  label={submission.policyType}
                  backgroundColor="bg-blue-100"
                  textColor="text-blue-600"
                />
              ),
            },
            {
              label: t('submission.transactionType'),
              value: submission.transactionType,
            },
            {
              label: t('submission.placementType'),
              value: submission.placingType,
            },
            {
              label: t('submission.lineOfBusiness'),
              value: submission.lineOfBusiness,
            },
          ],
        },
        {
          title: t('submission.transactionDetailsAndClassification'),
          icon: <ClipboardIcon />,
          items: [
            {
              label: t('submission.transactionType'),
              value: submission.transactionType,
            },
            {
              label: t('submission.placementType'),
              value: submission.placingType,
            },
            {
              label: t('submission.lineOfBusiness'),
              value: submission.lineOfBusiness,
            },
          ],
        },
        {
          title: t('submission.submissionRegistration'),
          icon: <UserTagIcon />,
          items: [
            {
              label: t('submission.assignedHandler'),
              value: submission.assignedHandlerId ? (
                <UserInfo
                  name={
                    !isLoadingAssignedHandler
                      ? assignedHandler?.name || '-'
                      : '-'
                  }
                />
              ) : (
                '-'
              ),
            },
            {
              label: t('submission.namedUnderwriter'),
              value: submission.namedUnderwriterId ? (
                <UserInfo
                  name={
                    !isLoadingNamedUnderwriter
                      ? namedUnderwriter?.name || '-'
                      : '-'
                  }
                />
              ) : (
                '-'
              ),
            },
            {
              label: t('submission.inceptionDate'),
              value: submission.inceptionDate
                ? getLocalDateForMidnightUTC(submission.inceptionDate).format(
                    'DD/MM/YYYY'
                  )
                : '-',
            },
            {
              label: t('submission.expiryDate'),
              value: submission.expiryDate
                ? getLocalDateForMidnightUTC(submission.expiryDate).format(
                    'DD/MM/YYYY'
                  )
                : '-',
            },
            {
              label: t('insured.insured'),
              value: !isLoadingInsured ? insured?.name || '-' : '-',
            },
            {
              label: t('submission.principalOccupation'),
              value: submission.principalOccupation || '-',
            },
            {
              label: t('submission.mainRegionAndRiskTerritory'),
              value: submission.mainRegionAndRiskTerritory || '-',
            },
            {
              label: t('submission.keyRiskLocation'),
              value: submission.keyRiskLocation || '-',
            },
          ],
        },
        {
          title: t('submission.fileUpload'),
          icon: <DocumentIcon />,
          items: [],
          fileData: {
            fileList: fileList,
            sessionId: sessionId,
            entityType: entityType,
          },
        },
      ],
    }),
    [
      t,
      submission.status,
      submission.policyType,
      submission.transactionType,
      submission.placingType,
      submission.lineOfBusiness,
      submission.assignedHandlerId,
      submission.namedUnderwriterId,
      submission.inceptionDate,
      submission.expiryDate,
      submission.principalOccupation,
      submission.mainRegionAndRiskTerritory,
      submission.keyRiskLocation,
      isLoadingAssignedHandler,
      assignedHandler?.name,
      isLoadingNamedUnderwriter,
      namedUnderwriter?.name,
      isLoadingInsured,
      insured?.name,
      fileList,
      sessionId,
      entityType,
    ]
  );

  // Dynamic button text based on countdown state
  const getContinueButtonText = useCallback(() => {
    if (autoNavigateCountdown !== null && autoNavigateCountdown > 0) {
      return `${t('general.continue')} (${autoNavigateCountdown})`;
    }
    return t('general.continue');
  }, [autoNavigateCountdown, t]);

  return (
    <div className="flex justify-center items-center min-h-screen w-full">
      <ReviewSummary
        onEdit={onEdit}
        onCancel={onCancel}
        onCreate={() => setShowCheck(true)}
        summaryData={summaryData}
        okText={t('general.continue')}
        cancelText={t('submission.cancelSubmission')}
      />

      <InfoModal
        isOpen={showCheck}
        onClose={hideShowCheckAndResetCountDown}
        onConfirm={handleSubmissionCheckResult}
        title={t('submission.validationCheck')}
        okText={getContinueButtonText()}
        subTitle={t('submission.systemVerification')}
        size="medium"
        isDisabled={Object.values(statusChecks).some(
          (status) => status === 'Pending'
        )}
      >
        <ValidationItem
          title={t('submission.duplicateCheck')}
          icon={<CopyIcon />}
          getValidationStatus={getDuplicateCheckStatus}
          setStatusCheck={setStatusChecks}
          statusKey="duplicateCheck"
        />
      </InfoModal>

      <ConfirmationModal
        isOpen={showProceedCreate}
        onClose={() => setShowProceedCreate(false)}
        onConfirm={proceedCreateSubmission}
        title={t('submission.submissionUnderReview')}
        okText={t('general.yesProceed')}
        cancelText={t('general.no')}
        content={t('submission.issueWithSubmissionCheckConfirmation')}
      />

      <InfoModal
        isOpen={showUnderReview}
        onClose={closeUnderReviewAndNavigate}
        onConfirm={closeUnderReviewAndNavigate}
        title={t('submission.submissionUnderReview')}
        okText={t('general.okay')}
      >
        {t('submission.issueWithSubmissionCheckAlert')}
      </InfoModal>
    </div>
  );
};
